import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getDownloadPrimaryClickAction } from '../../Audio/AudioUtils';
import {
  setPendingPlayAudio,
  setPendingPlayNext,
  setPendingPlayPrev,
} from '../../Audio/actions/AudioActions';
import {
  selectCurrentTime,
  selectAudioMeta,
  selectIsPlaying,
  selectIsPaused,
} from '../../Audio/selectors/audioSelectors';
import AudioDownloadButton from '../../app/Search/components/AudioDownloadButton/AudioDownloadButton';
import {
  selectSearchFilterOptions,
  selectSearchLogParameters,
} from '../../app/Search/selectors/searchSelectors';
import {
  selectHasAudioSubscription,
  selectPrimarySubscription,
} from '../../auth/AuthSelectors';
import AudioPlayerArtists from '../../common/components/AudioPlayer/AudioPlayerArtists';
import AudioPlayerDuration from '../../common/components/AudioPlayer/AudioPlayerDuration';
import AudioPlayerFavoriteButton from '../../common/components/AudioPlayer/AudioPlayerFavoriteButton';
import AudioPlayerForwardButton from '../../common/components/AudioPlayer/AudioPlayerForwardButton';
import AudioPlayerPlayButton from '../../common/components/AudioPlayer/AudioPlayerPlayButton';
import AudioPlayerRewindButton from '../../common/components/AudioPlayer/AudioPlayerRewindButton';
import AudioPlayerTitle from '../../common/components/AudioPlayer/AudioPlayerTitle';
import AudioPlayerVolumeControl from '../../common/components/AudioPlayer/AudioPlayerVolumeControl';
import AudioPlayerWaveform from '../../common/components/AudioPlayer/AudioPlayerWaveform';
import SearchSimilarPopover from '../../common/components/Popover/SearchSimilarPopover';
import SignUpForMakerPopover from '../../common/components/Popover/SignUpForMakerPopover';
import { StockItemContext } from '../../common/types/StockItemTypes';
import { getSliderValue } from '../../common/utils';

import './global-player.less';

const MobileProgress = () => {
  const {
    stockItem: { duration },
  } = useSelector(selectAudioMeta);
  const currentTime = useSelector(selectCurrentTime);
  const progress = (currentTime / duration) * 100;

  return (
    <div
      className="w-full h-1 bg-gray-300 top-0 left-0 absolute transform -translate-y-1 lg:hidden"
      style={{
        background: `-webkit-linear-gradient(left, #42A1FF ${progress}%, #D4D6D9 ${progress}%)`,
      }}
    />
  );
};

const PlaybackControls = () => {
  const dispatch = useDispatch();
  const { stockItem, stockItemArtists, stockItemFormats, waveformData } =
    useSelector(selectAudioMeta);

  return (
    <div className="grid grid-flow-col items-center gap-x-4 lg:gap-x-3 lg:mr-6">
      <div className="flex items-center hidden lg:flex cursor-pointer">
        <AudioPlayerRewindButton
          iconClassName="lg:w-5 lg:h-5 text-gray-200"
          // @ts-expect-error dispatch fn != AnyAction
          onClick={() => dispatch(setPendingPlayPrev())}
        />
      </div>

      <AudioPlayerPlayButton
        waveformData={waveformData}
        stockItem={stockItem}
        stockItemArtists={stockItemArtists}
        stockItemFormats={stockItemFormats}
        className="px-0 lg:px-4"
        wrapperClassName="w-10 h-10 lg:w-13 lg:h-13 bg-gray-600"
        context={StockItemContext.GLOBAL_PLAYER}
      />

      <div className="flex items-center cursor-pointer">
        <AudioPlayerForwardButton
          iconClassName="w-4 h-4 lg:w-5 lg:h-5 text-gray-200"
          // @ts-expect-error dispatch fn != AnyAction
          onClick={() => dispatch(setPendingPlayNext())}
        />
      </div>
    </div>
  );
};

const ActionButtons = () => {
  const dispatch = useDispatch();

  const { stockItemFormats, stockItem } = useSelector(selectAudioMeta);
  const audioSubscription = useSelector(selectHasAudioSubscription);
  const primarySubscription = useSelector(selectPrimarySubscription);
  const selectedSearchFilterOptions = useSelector(selectSearchFilterOptions);
  const searchLogParameters = useSelector(selectSearchLogParameters);

  return (
    <div className="grid grid-flow-col justify-end lg:justify-start items-center gap-x-6 lg:ml-6">
      <SignUpForMakerPopover
        mediaType="audio"
        stockItemId={stockItem.id}
        iconClassName="w-6 h-5 fill-current text-gray-200"
      />
      <SearchSimilarPopover
        stockItemId={stockItem.id}
        stockItemTitle={stockItem.title}
        selectedContentType={selectedSearchFilterOptions.contentType}
        className="hidden lg:flex"
        iconClassName="w-6 h-6 fill-current text-gray-200"
      />
      <AudioPlayerFavoriteButton
        stockItem={stockItem}
        stockItemFormats={stockItemFormats}
        iconClassName="w-6 h-6"
        alternateIconDefaultColor="text-gray-200"
        alternateIconInFolderColor="text-gray-200"
      />
      <AudioDownloadButton
        onDownloadOptionClick={(event) =>
          getDownloadPrimaryClickAction(
            event,
            stockItem,
            stockItemFormats,
            audioSubscription,
            primarySubscription,
            searchLogParameters,
            StockItemContext.GLOBAL_PLAYER,
            null,
            dispatch
          )
        }
        stockItem={stockItem}
        stockItemFormats={stockItemFormats}
        iconClassName="w-6 h-6 fill-current text-gray-200"
      />
    </div>
  );
};

const TitleAndArtists = () => {
  const { stockItem, stockItemArtists } = useSelector(selectAudioMeta);
  const isPlaying = useSelector(selectIsPlaying);
  const isPaused = useSelector(selectIsPaused);

  return (
    <div className="flex flex-col justify-center leading-none gap-y-1 lg:gap-y-2">
      <AudioPlayerTitle
        isPlaying={isPlaying}
        isPaused={isPaused}
        title={stockItem.title}
        detailsUrl={stockItem.detailsUrl}
        context={StockItemContext.GLOBAL_PLAYER}
        isCurrentSource={true}
        wrapperClassName="overflow-hidden overflow-ellipsis whitespace-nowrap text-white"
        className="text-xs lg:text-sm text-white hover:text-white"
      />
      {stockItemArtists.length ? (
        <AudioPlayerArtists
          stockItemArtists={stockItemArtists}
          stockItem={stockItem}
          context={StockItemContext.GLOBAL_PLAYER}
          wrapperClassName="text-white"
          className="text-xs lg:text-sm text-gray-100 text-white hover:text-white"
        />
      ) : null}
    </div>
  );
};

const CurrentDuration = () => {
  const currentTime = useSelector(selectCurrentTime);

  return (
    <AudioPlayerDuration
      duration={currentTime}
      className="text-white hidden lg:flex justify-end min-w-8"
    />
  );
};

const Waveform = () => {
  const dispatch = useDispatch();
  const currentTime = useSelector(selectCurrentTime);
  const { stockItem, stockItemArtists, stockItemFormats, waveformData } =
    useSelector(selectAudioMeta);

  const handleProgressClicked = React.useCallback(
    (event) => {
      const percentage = getSliderValue(event);
      const position = stockItem.duration * percentage;

      dispatch(
        // @ts-expect-error dispatch fn != AnyAction
        setPendingPlayAudio(
          stockItem.previewUrl,
          position,
          stockItem,
          stockItemArtists,
          stockItemFormats,
          waveformData,
          StockItemContext.GLOBAL_PLAYER
        )
      );
    },
    [dispatch, stockItem, waveformData, stockItemFormats, stockItemArtists]
  );

  return (
    <div className="hidden lg:flex overflow-hidden">
      <AudioPlayerWaveform
        waveformData={waveformData}
        stockItem={stockItem}
        currentTime={currentTime}
        isCurrentSource={true}
        handleProgressClicked={handleProgressClicked}
      />
    </div>
  );
};

const TotalDuration = () => {
  const { stockItem } = useSelector(selectAudioMeta);

  return (
    <AudioPlayerDuration
      duration={stockItem.duration}
      className="justify-start text-white hidden lg:flex lg:w-8"
    />
  );
};

const GlobalPlayerContainer = (): JSX.Element => {
  // add margin bottom to footer so global player does not overlap
  useEffect(() => {
    const footer = document.getElementsByClassName('footer');
    shouldRenderPlayer() && footer[0]
      ? footer[0].classList.add('mb-20')
      : footer[0].classList.remove('mb-20');
  }, []);

  const { stockItemFormats, stockItem } = useSelector(selectAudioMeta);

  const shouldRenderPlayer = () => stockItem && (stockItemFormats || []).length;

  if (!shouldRenderPlayer()) {
    return null;
  }

  return (
    <div className="grid grid-flow-col z-999 fixed bottom-0 w-screen bg-gray-700 py-2 px-4 lg:px-6 gap-x-8 lg:gap-x-4 h-14 lg:h-20">
      <MobileProgress />
      <PlaybackControls />
      <div className="flexible-elements grid gap-x-4 overflow-hidden">
        <TitleAndArtists />
        <CurrentDuration />
        <Waveform />
        <TotalDuration />
        <AudioPlayerVolumeControl
          className="hidden lg:grid ml-6"
          sliderClassName="w-18 m-0"
          iconClassName="w-6 h-6 fill-current text-gray-200"
        />
        <ActionButtons />
      </div>
    </div>
  );
};

export default GlobalPlayerContainer;
